import React from 'react';
import PropTypes from 'prop-types';

import Modal from './Modal';
import './CommandResultModal.css';

/**
 * Generic result surface for VfsChatPanel's slash commands (`/project`,
 * `/service`, `/runs`, `/cancel`, `/files`) — these are dispatched via the
 * chatbot-widget's `slashCommands` prop (see mia-chatbot-widget's
 * `SlashCommandDefinition`), whose `run(arg)` callback has no way to write a
 * message into the widget's own transcript, so results are shown here
 * instead, in the app's own existing `Modal` component (not antd's — this
 * one already carries this app's design tokens, see Modal.css).
 *
 * `result` is `null` when nothing is open. Otherwise:
 *   { title, kind: 'list', loading, error, items: [{ key, label, sublabel, onSelect }] }
 *   { title, kind: 'text', loading, error, text }
 *   { title, kind: 'info', message }
 */
export default function CommandResultModal({ result, onClose }) {
    if (!result) {
        return null;
    }

    const { title, kind, loading, error, items, text, message } = result;

    return (
        <Modal
            className="command-result-modal"
            isOpen
            onClose={onClose}
            showCancel={false}
            title={title}
            type="custom"
        >
            <div className="command-result-body">
                {loading && <div className="command-result-status">Loading…</div>}
                {!loading && error && <div className="command-result-status command-result-error">{error}</div>}
                {!loading && !error && kind === 'info' && (
                    <div className="command-result-status">{message}</div>
                )}
                {!loading && !error && kind === 'text' && (
                    <pre className="command-result-text">{text}</pre>
                )}
                {!loading && !error && kind === 'list' && (
                    items.length === 0 ? (
                        <div className="command-result-status">Nothing to show.</div>
                    ) : (
                        <ul className="command-result-list">
                            {items.map((item) => (
                                <li key={item.key}>
                                    <button
                                        className="command-result-item"
                                        disabled={!item.onSelect}
                                        onClick={item.onSelect}
                                        type="button"
                                    >
                                        <span className="command-result-item-label">{item.label}</span>
                                        {item.sublabel && (
                                            <span className="command-result-item-sublabel">{item.sublabel}</span>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )
                )}
            </div>
        </Modal>
    );
}

CommandResultModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    result: PropTypes.shape({
        error: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            onSelect: PropTypes.func,
            sublabel: PropTypes.string,
        })),
        kind: PropTypes.oneOf(['list', 'text', 'info']).isRequired,
        loading: PropTypes.bool,
        message: PropTypes.string,
        text: PropTypes.string,
        title: PropTypes.string.isRequired,
    }),
};
